/* =====================================================================
 * Layer 1 — Palette (raw colors)
 *
 * The lowest-level colour primitives. Every higher layer (semantic,
 * component, theme) must ultimately resolve to a value from this file.
 * Tenants can override any palette entry via their TenantManifest and
 * the theme factory will propagate the change through all layers.
 *
 * The B2C baseline below is bit-for-bit identical to the legacy
 * askyourai lightTheme / darkTheme colour blocks. Changing a value
 * here changes what askyour.ai renders — so don't.
 * ===================================================================== */

/** Palette for light mode. Every key matches a tenant-overridable slot. */
export const lightPalette = {
  /** Surface / background layers, light → dark */
  surface: {
    canvas: '#ffffff',
    raised: '#f7f7f8',
    sunken: '#f0f0f0',
    sidebar: '#f9f9f9',
    input: '#f4f4f4',
    inputHover: '#efefef',
    hover: '#ececec',
    code: '#f6f8fa',
    codeHeader: '#e8e8e8',
    userMessage: '#f7f7f8',
    assistantMessage: '#ffffff',
    tooltip: '#0d0d0d',
    overlay: 'rgba(0, 0, 0, 0.5)',
  },
  /** Foreground / text */
  text: {
    primary: '#0d0d0d',
    secondary: '#6e6e80',
    muted: '#8e8ea0',
    inverse: '#ffffff',
    link: '#2b2b2b',
  },
  /** Borders / dividers */
  border: {
    default: '#e5e5e5',
    light: '#f0f0f0',
    input: '#d9d9e3',
  },
  /**
   * Accent — neutral action/focus colour. Matches the ChatGPT-style
   * OpenAI convention where the "accent" is actually a near-black in
   * light mode and a near-white in dark mode. Used for focus rings,
   * primary action text, selection highlight.
   */
  accent: {
    base: '#0d0d0d',
    hover: '#2b2b2b',
    light: 'rgba(13, 13, 13, 0.06)',
  },
  /**
   * Brand — the vivid orange accent used as the page-level point
   * colour (auth / onboarding / pricing / cookie banner / visual
   * material / streaming pulse / sidebar cards). Distinct from
   * `accent` so tenants can override the brand highlight without
   * touching the neutral action language.
   */
  brand: {
    base: '#f97316',
    hover: '#ea580c',
    softBg: 'rgba(249, 115, 22, 0.1)',
    gradientEnd: '#ffb067',
  },
  /** Feedback / status */
  feedback: {
    success: '#10a37f',
    error: '#ef4444',
    errorBg: '#fef2f2',
    warning: '#f59e0b',
    warningBg: '#fffbeb',
    info: '#3b82f6',
    infoBg: '#eff6ff',
  },
  /** Usage bars (quota widgets) */
  usage: {
    healthy: '#10a37f',
    caution: '#c9942e',
    critical: '#ef4444',
    neutral: 'rgba(0,0,0,0.15)',
  },
  /** Scrollbars */
  scrollbar: {
    thumb: '#c5c5d2',
    thumbHover: '#9e9eb1',
  },
} as const;

/** Palette for dark mode. Same keys, different raw values. */
export const darkPalette = {
  surface: {
    canvas: '#212121',
    raised: '#2f2f2f',
    sunken: '#424242',
    sidebar: '#171717',
    input: '#2f2f2f',
    inputHover: '#383838',
    hover: '#3a3a3a',
    code: '#1e1e2e',
    codeHeader: '#2d2d3d',
    userMessage: '#2f2f2f',
    assistantMessage: '#212121',
    tooltip: '#f7f7f8',
    overlay: 'rgba(0, 0, 0, 0.7)',
  },
  text: {
    primary: '#ececec',
    secondary: '#b4b4b4',
    muted: '#8e8ea0',
    inverse: '#0d0d0d',
    link: '#d9d9d9',
  },
  border: {
    default: '#424242',
    light: '#333333',
    input: '#565869',
  },
  accent: {
    base: '#ececec',
    hover: '#d9d9d9',
    light: 'rgba(236, 236, 236, 0.08)',
  },
  /**
   * Brand — kept identical to light mode for now; the B2C app uses
   * the same #f97316 in both themes today. Adjust per visual review
   * once the dark screens are re-checked.
   */
  brand: {
    base: '#f97316',
    hover: '#ea580c',
    softBg: 'rgba(249, 115, 22, 0.1)',
    gradientEnd: '#ffb067',
  },
  feedback: {
    success: '#10a37f',
    error: '#f87171',
    errorBg: '#3b1c1c',
    warning: '#fbbf24',
    warningBg: '#3b2e1c',
    info: '#60a5fa',
    infoBg: '#1c2b3b',
  },
  usage: {
    healthy: '#10a37f',
    caution: '#c9942e',
    critical: '#f87171',
    neutral: 'rgba(255,255,255,0.2)',
  },
  scrollbar: {
    thumb: '#555555',
    thumbHover: '#777777',
  },
} as const;

/**
 * Widen `as const` literal string types to plain `string` so
 * `lightPalette` and `darkPalette` (different literal values) share a
 * single assignable Palette shape.
 */
type WidenStrings<T> = {
  readonly [K in keyof T]: T[K] extends string
    ? string
    : T[K] extends object
      ? WidenStrings<T[K]>
      : T[K];
};

export type Palette = WidenStrings<typeof lightPalette>;
export type ThemeMode = 'light' | 'dark';

export const basePalettes: Readonly<Record<ThemeMode, Palette>> = {
  light: lightPalette,
  dark: darkPalette,
};
